'use client';

import { useCallback, useState } from 'react';

import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { hasChannelSubscriptionAccess } from '../functions/access/hasChannelSubscriptionAccess';
import { joinChannel } from '../functions/members/joinChannel';
import type { CommunityChannel } from '../types/CommunityChannel';

/**
 * Join action for the current viewer + a channel — the web port of PopshopRN's
 * `useJoinCommunityChannel`. Gates on subscription access first: a public
 * channel is readable by anyone, but *joining* still requires meeting the
 * channel's `minimumSubscriptionLevel`. When the viewer lacks it, this routes
 * them to the subscribe blocker (`onRequireChannelSubscription`) instead of
 * POSTing a doomed add-member call. With access, it POSTs the add-member call
 * and refreshes the channel list; the live membership doc subscription
 * (`useMyChannelMembership`) then flips the gate to the message pane. Returns a
 * `{ join, joining, error, clearError }` tuple for the join CTA.
 *
 * A LOGGED-OUT viewer can't join — tapping the CTA routes them to login
 * (`?next=<here>`), so they land back on the channel and can join once signed in.
 */
export function useJoinChannel(channel: CommunityChannel | null | undefined) {
  const {
    creatorId,
    creatorInfo,
    refreshChannels,
    viewerSubscriptionLevel,
    onRequireChannelSubscription,
  } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  const login = useLogin();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const join = useCallback(async () => {
    // Auth still resolving, or the channel/creator isn't in scope yet — no-op
    // (lazy-auth: `undefined` = resolving; acting now could misroute a viewer
    // whose session is about to restore).
    if (currentUserId === undefined || !creatorId || !channel) return;

    // Logged out: joining needs an account. Route to login (durable `?next=`
    // returns them here) instead of silently doing nothing, branding the modal
    // with the creator so it reads "signing in to <creator>".
    if (currentUserId === null) {
      login(creatorInfo ? { creatorInfo } : undefined);
      return;
    }

    // Joining is subscription-gated even for public (readable) channels. Route
    // to the subscribe blocker when the viewer doesn't meet the level yet.
    if (
      !hasChannelSubscriptionAccess(
        viewerSubscriptionLevel,
        channel.minimumSubscriptionLevel,
      )
    ) {
      onRequireChannelSubscription?.(channel);
      return;
    }

    setJoining(true);
    setError(null);
    try {
      await joinChannel({
        creatorId,
        channelId: channel.id,
        userId: currentUserId,
      });
      await refreshChannels();
    } catch (cause: unknown) {
      const joinError =
        cause instanceof Error ? cause : new Error(String(cause));
      console.error('Failed to join community channel:', joinError);
      setError(joinError);
    } finally {
      setJoining(false);
    }
  }, [
    creatorId,
    creatorInfo,
    currentUserId,
    channel,
    login,
    refreshChannels,
    viewerSubscriptionLevel,
    onRequireChannelSubscription,
  ]);

  const clearError = useCallback(() => setError(null), []);

  return { join, joining, error, clearError };
}
